import { headerAndFooter as config } from '@config';

import PageHeading from './page-heading';

export default class HeadingPropagator {
  #currentPageHeading;

  constructor (currentPageHeading) {
    this.#currentPageHeading = currentPageHeading;
  }

  // This is a temporary solution, will fix in page-worker in the future.
  async forceUpdateHeadingOnAllPages (level) {
    if (!level) {
      INKIVE.EditorFacade.trigger('heading-propagation-finished');
      return;
    }

    const pagesOnCurrentRange = INKIVE.EditorFacade.get('header_and_footer', 'range')?.pages?.filter(id => id !== INKIVE.Tool._current_design_id);

    await this.#iterateUntilLevelFound(level, pagesOnCurrentRange, page => this.#updateHeadingOnPage(page, level), true);

    INKIVE.EditorFacade.trigger('heading-propagation-finished');
  }

  async #processHeadingContentOnPage (page, level) {
    const svgWrapper = svgedit.utilities.text2xml(page.content);
    let headings = [...svgWrapper.querySelectorAll(`[data-heading="${level}"]`)]
      .sort((a, b) => this.#inverseSortHeadingsByY(a, b)).reverse();

    if (!headings.length) {
      const previousPageWithKnownHeading = await this.#iterateUntilLevelFound(
        level, this.#getPreviousPages(page.id).reverse()
      );

      if (!previousPageWithKnownHeading) return;

      if (previousPageWithKnownHeading) {
        const svgOfPreviousPage = svgedit.utilities.text2xml(previousPageWithKnownHeading.content);
        headings = [...svgOfPreviousPage.querySelectorAll(`[data-heading="${level}"]`)]
          .sort((a, b) => this.#inverseSortHeadingsByY(a, b));
      }
    }

    const textNode = headings[0].tagName === 'g'
      ? headings[0]
      : headings[0].closest('g.textarea');

    const isPartialHeadings = !textNode.hasAttribute('data-heading');
    const attr = this.#mapNodeAttributes(textNode);
    const content = isPartialHeadings
      ? this.#getBottomPartialHeadingsContent(level, textNode)
      : attr.content;

    return { content, attr };
  }

  async #updateHeadingOnPage (page, level) {
    const settings = INKIVE.EditorFacade.get('header_and_footer', 'range', page.id);
    const expectsHeadingOnThatLevel = settings.headingSection && settings.headingLevel === level;

    if (!expectsHeadingOnThatLevel) return;

    const data = await this.#processHeadingContentOnPage(page, level);

    if (data) {
      this.#processDownwardsPropagation(page, level, data.content, () => data.attr);
    } else {
      this.#processRemoveContentDownwardsPropagation(page, level);
    }
  }

  // The main heading of a given level has changed, and we have an id
  propagateIdUpwards (level, id, content) {
    const data = INKIVE.DesignFacade.get(id);
    const isPartialHeadings = !data.heading;
    const textContent = isPartialHeadings
      ? this.#getTopPartialHeadingsContent(level, data)
      : content || data?.content;

    this.#currentPageHeading.hasPageHeadingLevel(level) &&
      this.#currentPageHeading.updatePageHeading(
        textContent, () => this.#mapFacadeAttributes(data)
      );
  }

  // The main heading of a given level has changed, and we have all details
  propagateFacadeDataUpwards (level, data) {
    const isPartialHeadings = !data.heading;
    const content = isPartialHeadings
      ? this.#getTopPartialHeadingsContent(level, data)
      : data.content;

    this.#currentPageHeading.hasPageHeadingLevel(level) &&
      this.#currentPageHeading.updatePageHeading(
        content, () => this.#mapFacadeAttributes(data)
      );
  }

  // The main heading of a given level has changed, and we have a node (probably from previous page)
  propagateNodeUpwards (level, node) {
    const isPartialHeadings = !node.hasAttribute('data-heading');
    const attr = this.#mapNodeAttributes(node);
    const content = isPartialHeadings
      ? this.#getBottomPartialHeadingsContent(level, node)
      : attr.content;
    this.#currentPageHeading.hasPageHeadingLevel(level) &&
      this.#currentPageHeading.updatePageHeading(content, () => attr);
  }

  // The bottom heading of a given level has changed, and we have an id
  propagateIdDownwards (level, id, content) {
    const data = INKIVE.DesignFacade.get(id);
    const isPartialHeadings = !data.heading;
    const textContent = isPartialHeadings
      ? this.#getTopPartialHeadingsContent(level, data)
      : content || data?.content;

    this.#propagateDownwards(level, textContent,
      () => this.#mapFacadeAttributes(data)
    );
  }

  // The bottom heading of a given level has changed, and we have a node (probably from previous page)
  propagateNodeDownwards (level, node) {
    const isPartialHeadings = !node.hasAttribute('data-heading');
    const attr = this.#mapNodeAttributes(node);
    const content = isPartialHeadings
      ? this.#getBottomPartialHeadingsContent(level, node)
      : attr.content;
    this.#propagateDownwards(level, content, () => attr);
  }

  // There was a heading in this page, and now there is none
  async onUnknownHeading (level) {
    const previousPageWithKnownHeading = await this.#iterateUntilLevelFound(
      level, this.#getPreviousPages().reverse()
    );

    if (previousPageWithKnownHeading) {
      const svgWrapper = svgedit.utilities.text2xml(previousPageWithKnownHeading.content);
      const headings = [...svgWrapper.querySelectorAll(`[data-heading="${level}"]`)]
        .sort((a, b) => this.#inverseSortHeadingsByY(a, b));

      if (!headings.length) throw new Error('Expected heading not found');

      const textNode = headings[0].tagName === 'g'
        ? headings[0]
        : headings[0].closest('g.textarea');
      this.propagateNodeUpwards(level, textNode);
      this.propagateNodeDownwards(level, textNode);
    } else {
      this.#currentPageHeading.hasPageHeadingLevel(level) &&
        this.#currentPageHeading.removePageHeading();

      this.#removeContentDownwards(level);
    }
  }

  #inverseSortHeadingsByY (a, b) {
    const rectA = a.querySelector('rect') || a;
    const rectB = b.querySelector('rect') || b;

    return rectA?.getAttribute('y') > rectB?.getAttribute('y') ? -1 : 1;
  }

  async #propagateDownwards (level, content, headingAttributesGetter) {
    await this.#iterateUntilLevelFound(level, this.#getFollowingPages(), page => {
      const settings = INKIVE.EditorFacade.get('header_and_footer', 'range', page.id);
      const expectsHeadingOnThatLevel = settings.headingSection && settings.headingLevel === level;

      expectsHeadingOnThatLevel && this.#processDownwardsPropagation(
        page, level, content, headingAttributesGetter
      );
    });
  }

  async #removeContentDownwards (level) {
    await this.#iterateUntilLevelFound(level, this.#getFollowingPages(), page => {
      const settings = INKIVE.EditorFacade.get('header_and_footer', 'range', page.id);
      const expectsHeadingOnThatLevel = settings.headingSection && settings.headingLevel === level;

      expectsHeadingOnThatLevel && this.#processRemoveContentDownwardsPropagation(page, level);
    });
  }

  async #processRemoveContentDownwardsPropagation (page, level) {
    const svgWrapper = svgedit.utilities.text2xml(page.content);
    const layoutSection = svgWrapper.getElementById(this.#currentPageHeading.getLayoutSectionId());

    if (layoutSection) {
      const pageHeading = new PageHeading();
      pageHeading.load(level, layoutSection);
      pageHeading.removePageHeading();

      await INKIVE.PageFactory.savePageState(page.id, INKIVE.Helpers.serializeToString(svgWrapper));
    } else {
      throw new Error('Headers and Footers layout not found!');
    }
  }

  async #processDownwardsPropagation (page, level, content, headingAttributesGetter) {
    const svgWrapper = svgedit.utilities.text2xml(page.content);
    const layoutSection = svgWrapper.getElementById(this.#currentPageHeading.getLayoutSectionId());

    if (layoutSection) {
      const pageHeading = new PageHeading();
      pageHeading.load(level, layoutSection);
      pageHeading.updatePageHeading(content, headingAttributesGetter);

      await INKIVE.PageFactory.savePageState(page.id, INKIVE.Helpers.serializeToString(svgWrapper));
    } else {
      throw new Error('Headers and Footers layout not found!');
    }
  }

  async #iterateUntilLevelFound (level, pages, callback = () => {}, force = false) {
    for (const p of pages) {
      const page = await INKIVE.PageFactory.getPage(p.id || p);
      const haveHeadingsOnThatLevel = page.content.includes(`data-heading="${level}"`);
      if (!force && haveHeadingsOnThatLevel) return page;
      await callback(page);
    }
  }

  #getPreviousPages (pageId = null) {
    const pages = INKIVE.ProjectFacade.get('designs');
    const currentPageId = pageId || INKIVE.Tool._current_design_id;
    const currentPage = pages.find(p => p.id === currentPageId);

    return pages.filter(p => currentPage.page_number > p.page_number);
  }

  #getFollowingPages () {
    const pages = INKIVE.ProjectFacade.get('designs');
    const currentPage = pages.find(p => p.id === INKIVE.Tool._current_design_id);

    return pages.filter(p => currentPage.page_number < p.page_number);
  }

  #mapFacadeAttributes (data) {
    return {
      font: data.font.family,
      color: data.color,
      anchor: data.anchor,
      content: data.content
    };
  }

  #mapNodeAttributes (node) {
    const textGroup = node.querySelector('g.text') || node.closest('g.text');

    return {
      font: textGroup.getAttribute('font-family'),
      color: textGroup.getAttribute('fill') || '#000',
      anchor: textGroup.getAttribute('text-anchor') || 'start',
      content: getContent(node)
    };

    function getContent (node) {
      return node.nodeType === 3
        ? node.nodeValue
        : [...node.childNodes].map(getContent).join('');
    }
  }

  propagatePageHeadingSettings () {
    const rangeSettings = INKIVE.EditorFacade.get('header_and_footer', 'range');
    const existPageHeading = document.querySelector(config.heading.attribute.selector);

    if (!rangeSettings.headingSection && existPageHeading) {
      existPageHeading.remove();
    }
  }

  #getTopPartialHeadingsContent (level, element) {
    const currentLevelStrings = element.text.strings
      .filter(string => Number(string.heading) === level);
    const firstEolIndex = currentLevelStrings.findIndex(string => string.isEol);
    let content = '';

    for (let i = 0; i <= firstEolIndex; i++) {
      content += currentLevelStrings[i].textContent;
    }

    return content;
  }

  #getBottomPartialHeadingsContent (level, node) {
    const data = INKIVE.DesignFacade.get(node);
    const currentLevelStrings = data.text.strings
      .filter(string => Number(string.heading) === level);
    const lastEolIndex = currentLevelStrings.findLastIndex(string => string.isEol);

    let content = currentLevelStrings[lastEolIndex].textContent;

    for (let i = lastEolIndex - 1; i >= 0; i--) {
      if (currentLevelStrings[i].isEol) break;

      content = currentLevelStrings[i].textContent + content;
    }

    return content;
  }
}
